import { FunctionComponent, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Flex, Table, TableProps } from 'antd';
import dayjs from 'dayjs';

// components
import StatusComponent from 'components/status/status';
import ListButton from 'components/listButton/listButton';
import NoData from 'components/noData/noData';

// models
import { CompanyShareholder } from '../models/shareholder';

// services
import NotificationService from 'core/services/notification';
import { onDownloadCompanyShareholderExcel, onDownloadCompanyShareholderList, onGetCompanyShareholderLists } from '../services/shareholdersServices';
import TableTotal from 'components/tableTotal/tableTotal';
import { isRTL } from 'core/services/language';
import { StatusColors } from 'utils/statusColors';
import { downloadFile } from 'utils/downloadFile';
import PageTitle from 'components/title/title';

// hooks
import useCompanySuspended from 'core/hooks/useCompanySuspended';

interface CompanyShareholdersListPageProps {}

const CompanyShareholdersListPage: FunctionComponent<CompanyShareholdersListPageProps> = () => {
	const { t } = useTranslation();

	const { suspendedProps } = useCompanySuspended();

	const navigate = useNavigate();
	const location = useLocation();

	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [currentPage, setCurrentPage] = useState<number>(1);
	const [totalRecords, setTotalRecords] = useState<number>(0);
	const [lists, setLists] = useState<CompanyShareholder[]>([]);

	const columns: TableProps<CompanyShareholder>['columns'] = [
		{
			title: t('COMMON.CREATED_AT'),
			dataIndex: 'date',
			key: 'date',
			render: (date) => <span dir="ltr">{dayjs(date).format('DD/MM/YYYY, hh:mm A')}</span>,
		},
		{
			title: t('COMMON.STATUS'),
			dataIndex: 'status',
			key: 'status',
			render: (status) => <StatusComponent text={t(`COMMON.${status?.toUpperCase()}`)} color={StatusColors[status]} />,
		},
		{
			title: t('COMMON.UPDATED_AT'),
			dataIndex: 'updated_at',
			key: 'updated_at',
			render: (updatedAt) => <span dir="ltr">{dayjs(updatedAt).format('DD/MM/YYYY, hh:mm A')}</span>,
		},
		{
			title: t('COMMON.TRANSACTIONS'),
			dataIndex: 'transactions_count',
			key: 'transactions_count',
		},
		{
			title: t('COMMON.SHAREHOLDERS'),
			dataIndex: 'shareholders_count',
			key: 'shareholders_count',
		},
		{
			title: t('COMMON.ACTIONS'),
			dataIndex: 'actions',
			key: 'actions',
			render: (_, record) =>
				record?.status === 'Success' && (
					<Flex gap={10} align="center" justify="center">
						<ListButton type="download" {...suspendedProps} onClick={() => downloadShareholderList(record.id)} />
						{record.excel_downloadable && (
							<ListButton type="excel" {...suspendedProps} onClick={() => downloadShareholderExcel(record.id)} svgHeight={28} svgWidth={28} />
						)}
					</Flex>
				),
		},
	];

	useEffect(() => {
		setCurrentPage(getCurrentPage());

		getShareholderList();
	}, []);

	const getCurrentPage = () => {
		const params = new URLSearchParams(location.search);
		const page = params.get('page');
		return page ? parseInt(page) : 1;
	};

	const getShareholderList = async () => {
		try {
			setIsLoading(true);

			const response = await onGetCompanyShareholderLists();

			setLists(response['shareholder-lists']);
			setTotalRecords(response.pagination.total);

			setIsLoading(false);
		} catch (error) {
			setIsLoading(false);
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.FETCH_ERROR'));
		}
	};

	const fetchMoreData = async (page: number) => {
		try {
			const params = new URLSearchParams(location.search);
			params.set('page', page.toString());
			navigate(`${location.pathname}?${params.toString()}`, { replace: true });

			setIsLoading(true);
			window.scrollTo({ behavior: 'smooth', top: 0 });

			const response = await onGetCompanyShareholderLists(page);
			setLists(response['shareholder-lists']);

			setCurrentPage(page);
			setIsLoading(false);
		} catch (error) {
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.FETCH_ERROR'));
			setIsLoading(false);
		}
	};

	const downloadShareholderList = async (id: number) => {
		try {
			const { file_url } = await onDownloadCompanyShareholderList(id);

			await downloadFile(file_url, isRTL() ? 'قائمة المساهمين.pdf' : 'shareholder-list.pdf');
		} catch (error) {
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.DOWNLOAD_ERROR'));
		}
	};

	const downloadShareholderExcel = async (id: number) => {
		try {
			const { file } = await onDownloadCompanyShareholderExcel(id);

			await downloadFile(file.download_link, isRTL() ? 'قائمة المساهمين.xlsx' : 'shareholder-list.xlsx');
		} catch (error) {
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.DOWNLOAD_ERROR'));
		}
	};

	return (
		<>
			<PageTitle name={t('TITLE.SHAREHOLDERS_REGISTRY')} />

			<Table
				columns={columns}
				dataSource={lists}
				scroll={{ x: 600 }}
				rowKey="id"
				pagination={{
					pageSize: 8,
					total: totalRecords,
					showSizeChanger: false,
					current: currentPage,
					onChange: fetchMoreData,
					showTotal: (total) => <TableTotal total={total} />,
				}}
				loading={isLoading}
				className="bordered mt-4"
				locale={{ emptyText: <NoData /> }}
			/>
		</>
	);
};

export default CompanyShareholdersListPage;
